/* eslint-disable no-underscore-dangle, no-param-reassign -- legacy Mapbox marker helpers intentionally stash original palette / fitBounds flags on the live map objects */
import { common, orange } from '@mui/material/colors'
import { get, partition, sortBy } from 'lodash'

import { addRouteIdSuffix, ROUTE_COLOR_STOPS_DOT_PAINT, ROUTE_COLOR_STOPS_LAYOUT } from 'components/route-manager/RouteManagerMap/settings'
import { ROUTE_STOPS_LINE_STRING } from 'slices/route/routeSlice'
import T from 'T'
import { PRIMARY, WARNING } from 'theme/colors'
import { getDateObjectFromDateString } from 'utils/date'
import { getBounds, removeLayerIfExist, removeSourceIfExist } from 'utils/map'
import { isOnRequestStop } from 'utils/route'
import { PREVIEW_TYPE } from 'utils/routeStopOperations/constants'
import { formatRoutesWithContrastColors, formatGeojsonDataBetweenDays } from 'utils/routeStopOperations/featureFormatters'
import { getNewlyAssignedStops, getMovedStopsByDate } from 'utils/routeStopOperations/normalization'
import {
  toSequencePairs,
  buildBulkDailyAssignPayload as _buildBulkDailyAssignPayload,
  buildBulkDailyAssignPayloadByDate as _buildBulkDailyAssignPayloadByDate,
  buildMasterAssignPayload as _buildMasterAssignPayload,
  buildMasterResequenceFromDaily,
} from 'utils/routeStopOperations/payloads'
import { getPricingPeriod } from 'utils/service'
import { isValidHex } from 'utils/validations'
import { stopStatus } from 'utils/workOrderStatus'

export const BULK_MOVE_MAP_LAYERS = {
  FROM_ROUTE_LAYER: 'FROM_ROUTE_LAYER',
  TO_ROUTE_LAYER: 'TO_ROUTE_LAYER',
  BOTH_ROUTES_LAYER: 'BOTH_ROUTES_LAYER',
}

const { FROM_ROUTE_LAYER, TO_ROUTE_LAYER, BOTH_ROUTES_LAYER } = BULK_MOVE_MAP_LAYERS

export const STATIC_COLOR_STOPS_LAYER = 'static-color-stops-layer'
export const DYNAMIC_COLOR_STOPS_LAYER = 'dynamic-color-stops-layer'
export const STATIC_COLOR_DOT_LAYER = 'static-color-stops-dot-layer'
export const DYNAMIC_COLOR_DOT_LAYER = 'dynamic-color-stops-dot-layer'
export const STATIC_COLOR_STOPS_SOURCE = 'static-color-stops-source'
export const DYNAMIC_COLOR_STOPS_SOURCE = 'dynamic-color-stops-source'

const DOT_ZOOM_UPPER_LIMIT = 18
export const HEADER_HEIGHT = 42

export const ROW_HEIGHT = 104

export const ZOOM_DOT_LAYER_PROPS = {
  minzoom: 0,
  maxzoom: DOT_ZOOM_UPPER_LIMIT,
}

export const ZOOM_MARKER_LAYER_PROPS = {
  minzoom: DOT_ZOOM_UPPER_LIMIT + 0.01,
}

export const getBulkMoveStopColumnSx = theme => {
  return {
    flex: '0 0 368px',
    maxWidth: '368px',
    width: '368px',
    [theme.breakpoints.down('md')]: {
      flex: '0 0 368px',
      maxWidth: '368px',
      width: '368px',
    },
    [theme.breakpoints.up('md')]: {
      flex: '0 0 368px',
      maxWidth: '368px',
      width: '368px',
    },
    [theme.breakpoints.up('lg')]: {
      flex: '0 0 368px',
      maxWidth: '368px',
      width: '368px',
    },
    [theme.breakpoints.up(1700)]: { flex: '0 0 460px', maxWidth: '460px', width: '460px' },
    [theme.breakpoints.up(2200)]: { flex: '0 0 552px', maxWidth: '552px', width: '552px' },
  }
}

export const UNASSIGNED_MARKER_PROPS = {
  icon: 'to-be-assigned-marker',
  color: common.white,
  'route-text-color': common.black,
  'border-color': WARNING.main,
}

export const HIGHLIGHTED_MARKER_PROPS = {
  icon: 'highlighted-marker',
  color: PRIMARY.main,
  'route-text-color': common.black,
  'border-color': common.white,
}

export const removeLayersAndSources = (mapCurrentRef, routeId) => {
  removeLayerIfExist(mapCurrentRef, addRouteIdSuffix(ROUTE_STOPS_LINE_STRING, routeId))
  removeLayerIfExist(mapCurrentRef, addRouteIdSuffix(DYNAMIC_COLOR_STOPS_LAYER, routeId))
  removeLayerIfExist(mapCurrentRef, addRouteIdSuffix(DYNAMIC_COLOR_DOT_LAYER, routeId))
  removeSourceIfExist(mapCurrentRef, addRouteIdSuffix(DYNAMIC_COLOR_STOPS_SOURCE, routeId))
  removeLayerIfExist(mapCurrentRef, addRouteIdSuffix(STATIC_COLOR_STOPS_LAYER, routeId))
  removeLayerIfExist(mapCurrentRef, addRouteIdSuffix(STATIC_COLOR_DOT_LAYER, routeId))
  removeSourceIfExist(mapCurrentRef, addRouteIdSuffix(STATIC_COLOR_STOPS_SOURCE, routeId))
  removeSourceIfExist(mapCurrentRef, addRouteIdSuffix(ROUTE_STOPS_LINE_STRING, routeId))
}

export const addMarkerLayers = (
  mapCurrentRef,
  { sourcePrefix, dotLayerPrefix, markerLayerPrefix, routeId, beforeIdLayer, hasDotLayer = true }
) => {
  if (hasDotLayer) {
    mapCurrentRef.addLayer(
      {
        id: addRouteIdSuffix(dotLayerPrefix, routeId),
        type: 'circle',
        source: addRouteIdSuffix(sourcePrefix, routeId),
        paint: { ...ROUTE_COLOR_STOPS_DOT_PAINT, 'circle-stroke-color': ['get', 'border-color'] },
        ...ZOOM_DOT_LAYER_PROPS,
      },
      beforeIdLayer
    )
  }

  mapCurrentRef.addLayer(
    {
      id: addRouteIdSuffix(markerLayerPrefix, routeId),
      type: 'symbol',
      source: addRouteIdSuffix(sourcePrefix, routeId),
      layout: {
        ...ROUTE_COLOR_STOPS_LAYOUT,
        'icon-image': ['get', 'icon'],
      },
      paint: {
        'icon-color': ['get', 'color'],
        'text-color': ['get', 'route-text-color'],
      },
      ...(hasDotLayer && { ...ZOOM_MARKER_LAYER_PROPS }),
    },
    beforeIdLayer
  )
  mapCurrentRef.on('mouseenter', addRouteIdSuffix(dotLayerPrefix, routeId), () => {
    // eslint-disable-next-line no-param-reassign
    mapCurrentRef.getCanvas().style.cursor = 'pointer'
  })
  mapCurrentRef.on('mouseleave', addRouteIdSuffix(dotLayerPrefix, routeId), () => {
    // eslint-disable-next-line no-param-reassign
    mapCurrentRef.getCanvas().style.cursor = 'grab'
  })
  mapCurrentRef.on('mouseenter', addRouteIdSuffix(markerLayerPrefix, routeId), () => {
    // eslint-disable-next-line no-param-reassign
    mapCurrentRef.getCanvas().style.cursor = 'pointer'
  })
  mapCurrentRef.on('mouseleave', addRouteIdSuffix(markerLayerPrefix, routeId), () => {
    // eslint-disable-next-line no-param-reassign
    mapCurrentRef.getCanvas().style.cursor = 'grab'
  })
}

export const setLayerVisibility = (mapRef, layerId, visibility) => {
  if (mapRef.getLayer(layerId)) {
    mapRef.setLayoutProperty(layerId, 'visibility', visibility)
  } else {
    console.warn(`Layer with ID ${layerId} does not exist.`)
  }
}
export const hideLayers = (mapRef, routeId) => {
  setLayerVisibility(mapRef, addRouteIdSuffix(STATIC_COLOR_STOPS_LAYER, routeId), 'none')
  setLayerVisibility(mapRef, addRouteIdSuffix(STATIC_COLOR_DOT_LAYER, routeId), 'none')
  setLayerVisibility(mapRef, addRouteIdSuffix(DYNAMIC_COLOR_STOPS_LAYER, routeId), 'none')
  setLayerVisibility(mapRef, addRouteIdSuffix(DYNAMIC_COLOR_DOT_LAYER, routeId), 'none')
  setLayerVisibility(mapRef, addRouteIdSuffix(ROUTE_STOPS_LINE_STRING, routeId), 'none')
}

export const showLayers = (mapRef, routeId) => {
  setLayerVisibility(mapRef, addRouteIdSuffix(STATIC_COLOR_STOPS_LAYER, routeId), 'visible')
  setLayerVisibility(mapRef, addRouteIdSuffix(STATIC_COLOR_DOT_LAYER, routeId), 'visible')
  setLayerVisibility(mapRef, addRouteIdSuffix(DYNAMIC_COLOR_STOPS_LAYER, routeId), 'visible')
  setLayerVisibility(mapRef, addRouteIdSuffix(DYNAMIC_COLOR_DOT_LAYER, routeId), 'visible')
  setLayerVisibility(mapRef, addRouteIdSuffix(ROUTE_STOPS_LINE_STRING, routeId), 'visible')
}

export const toggleMoveBetweenRoutesLayers = (mapRef, currentLayer, moveFromRouteId, moveToRouteId) => {
  switch (currentLayer) {
    case FROM_ROUTE_LAYER:
      showLayers(mapRef, moveFromRouteId)
      hideLayers(mapRef, moveToRouteId)
      break

    case TO_ROUTE_LAYER:
      hideLayers(mapRef, moveFromRouteId)
      showLayers(mapRef, moveToRouteId)
      break

    case BOTH_ROUTES_LAYER:
    default:
      showLayers(mapRef, moveFromRouteId)
      showLayers(mapRef, moveToRouteId)
      break
  }
}

/**
 * Resets the fitBounds flag on the map instance to allow fitBounds to be triggered again
 * @param {Object} mapRef - The map reference
 */
export const resetFitBoundsFlag = mapRef => {
  if (mapRef && mapRef._hasFitBounds) {
    mapRef._hasFitBounds = false
  }
}

/**
 * Fits the map bounds to show all markers in the specified layers
 * @param {Object} mapRef - The map reference
 * @param {string} currentLayer - The current active layer
 * @param {Object} fromSequenceLine - The sequence line for the "from" route
 * @param {Object} toSequenceLine - The sequence line for the "to" route
 * @param {Object} props - Additional properties to pass to fitBounds
 * @param {boolean} shouldFitBounds - Whether to perform the fitBounds operation
 */
export const fitBoundsMoveBetweenRoutesLayers = (mapRef, currentLayer, fromSequenceLine, toSequenceLine, props, shouldFitBounds = true) => {
  // If shouldFitBounds is false, skip the fitBounds operation
  if (!shouldFitBounds) {
    return
  }

  const fromCoordinates = get(fromSequenceLine, 'geometry.coordinates', [])
  const toCoordinates = get(toSequenceLine, 'geometry.coordinates', [])

  let targetCoordinates

  switch (currentLayer) {
    case FROM_ROUTE_LAYER:
      targetCoordinates = fromCoordinates
      break

    case TO_ROUTE_LAYER:
      targetCoordinates = toCoordinates
      break

    case BOTH_ROUTES_LAYER:
    default:
      targetCoordinates = fromCoordinates.concat(toCoordinates)
      break
  }

  if (Array.isArray(targetCoordinates) && targetCoordinates.length > 0) {
    const bounds = getBounds(targetCoordinates, 25)
    mapRef.fitBounds(bounds, { maxZoom: 16, linear: true, ...props })

    // Store a flag on the map instance to indicate that fitBounds has been performed
    // This allows components to check if fitBounds has already happened
    mapRef._hasFitBounds = true
  }
}

// Read the pre-highlight color pair. Skip `properties.color` when the
// marker is currently highlighted — otherwise PRIMARY.main would become the
// "original" after repeated select/deselect cycles.
const readOriginalPalette = properties => {
  const isCurrentlyHighlighted = get(properties, 'icon') === HIGHLIGHTED_MARKER_PROPS.icon
  const snapshotColor = get(properties, '_originalColor')
  const snapshotContrast = get(properties, '_originalContrastColor')
  const liveColor = isCurrentlyHighlighted ? undefined : get(properties, 'color')
  const liveContrast = isCurrentlyHighlighted ? undefined : get(properties, 'route-text-color')
  return {
    originalColor: snapshotColor || liveColor,
    originalContrastColor: snapshotContrast || liveContrast,
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- counterpart of clearHighlight below; kept for symmetry until the highlight path migrates
const applyHighlight = properties => {
  const { originalColor, originalContrastColor } = readOriginalPalette(properties)
  return {
    ...properties,
    _originalColor: originalColor ?? properties._originalColor ?? properties.color,
    _originalContrastColor: originalContrastColor ?? properties._originalContrastColor ?? properties['route-text-color'],
    ...HIGHLIGHTED_MARKER_PROPS,
  }
}

// Always returns a resolved color pair + monochrome icon. Never returns the
// feature untouched — that leaves it stuck highlighted when routesById is empty.
const applyRestore = (properties, route) => {
  const { originalColor, originalContrastColor } = readOriginalPalette(properties)
  const color = get(route, 'color') || originalColor || common.black
  const contrastColor = get(route, 'contrastColor') || originalContrastColor || common.white
  return {
    ...properties,
    color,
    _originalColor: originalColor || color,
    icon: 'monochrome-marker',
    'route-text-color': contrastColor,
    _originalContrastColor: originalContrastColor || contrastColor,
    'border-color': contrastColor,
  }
}

// Selection no longer recolours the marker — the dispatcher gets a blue
// outer ring (via highlightedStopIds → BulkDeckMarkerLayer / StopMarker) and
// the pill itself stays in its natural assigned/unassigned treatment.
// `selectedToRows` / `selectedFromRows` are kept on the signature so existing
// call sites don't have to change, but they no longer affect the output shape.
export const updateToMarkers = (_selectedToRows, routesById, toMarkers, moveToRouteId) => {
  if (!Array.isArray(toMarkers)) return []
  return toMarkers.map(feature => {
    const properties = get(feature, 'properties', {})
    if (moveToRouteId === 'UNASSIGNED') {
      return {
        ...feature,
        properties: {
          ...properties,
          ...UNASSIGNED_MARKER_PROPS,
        },
      }
    }
    return { ...feature, properties: applyRestore(properties, get(routesById, moveToRouteId)) }
  })
}

export const updateFromMarkers = (_selectedFromRows, rowsById, fromMarkers) => {
  if (!Array.isArray(fromMarkers)) return []
  return fromMarkers.map(feature => {
    const properties = get(feature, 'properties', {})
    return { ...feature, properties: applyRestore(properties, get(rowsById, feature.properties.routeId)) }
  })
}

export const filterAndRepositionUnselectedRows = (rows, selectedRowIds) => {
  return rows.filter(row => !selectedRowIds.has(row.id)).map((row, index) => ({ ...row, position: index }))
}

export const combineAndResetPositionRows = (originalRows, additionalRows) => {
  return [...originalRows, ...additionalRows]
    .sort((a, b) => a.originalPosition - b.originalPosition)
    .map((row, index) => ({ ...row, position: index }))
}

export const combineAndRecolorMarkers = (originalMarkers, additionalMarkers, routesById, routeId, theme) => {
  return [...originalMarkers, ...additionalMarkers]
    .sort((a, b) => a.properties.originalPosition - b.properties.originalPosition)
    .map((feature, index) => {
      const properties = get(feature, 'properties', {})
      if (routeId === 'UNASSIGNED') {
        return {
          ...feature,
          properties: {
            ...properties,
            color: common.white,
            'border-color': orange[300],
            icon: 'to-be-assigned-marker',
            'route-text-color': common.black,
            position: index + 1,
          },
        }
      }
      const color = get(routesById, [routeId, 'color'], common.black)
      const formattedColor = isValidHex(color) ? color : common.black
      const routeTextColor = theme.palette.getContrastText(formattedColor)
      return {
        ...feature,
        properties: {
          ...properties,
          color,
          _originalColor: color,
          icon: 'monochrome-marker',
          'route-text-color': routeTextColor,
          _originalContrastColor: routeTextColor,
          'border-color': routeTextColor,
          position: index + 1,
        },
      }
    })
}

// Prefer id-based marker lookup over positional pairing — partitionMoveFromMarkers
// filters markers by properties.id, so a selected row whose marker never made it
// into moveFromRouteStopMarkers (e.g. off-screen / virtualized / missing coords)
// would leave the reordered arrays with mismatched lengths.
const buildMarkerLookup = markers => {
  const byId = new Map()
  markers.forEach((marker, idx) => {
    if (!marker) return
    const mid = get(marker, 'properties.id')
    if (mid != null) byId.set(mid, marker)
    byId.set(`__idx_${idx}__`, marker)
  })
  return byId
}

const lookupSourceMarker = (markersById, rowId, markersArray, idx) =>
  (rowId != null && markersById.get(rowId)) || markersArray[idx] || markersById.get(`__idx_${idx}__`)

const cloneMarkerForRow = sourceMarker => (sourceMarker ? { ...sourceMarker, properties: { ...(sourceMarker.properties || {}) } } : null)

const applyHighlightedMarker = markerItem => {
  if (!markerItem) return
  markerItem.properties = {
    ...markerItem.properties,
    icon: 'monochrome-marker',
    color: markerItem.properties._originalColor || markerItem.properties.color,
    'route-text-color': markerItem.properties._originalContrastColor || markerItem.properties['route-text-color'],
    'border-color': markerItem.properties._originalContrastColor || markerItem.properties['border-color'],
  }
}

const applyUnassignedMarker = markerItem => {
  if (!markerItem) return
  markerItem.properties = { ...markerItem.properties, ...UNASSIGNED_MARKER_PROPS }
}

const handleInsertedRowPosition = ({ rowItem, markerItem, rowId, formValues, selectedRowIdsMap, setValue, currentPosition }) => {
  const rowTypedPosition = get(formValues, rowId, '')
  const isPositioned = selectedRowIdsMap.has(rowId) || rowTypedPosition !== ''

  if (selectedRowIdsMap.has(rowId) || (rowTypedPosition !== '' && rowTypedPosition !== currentPosition + 1)) {
    setValue(rowId, currentPosition + 1)
  }

  rowItem.position = isPositioned ? currentPosition : ''
  if (markerItem) markerItem.properties.position = isPositioned ? currentPosition + 1 : ''

  if (isPositioned) applyHighlightedMarker(markerItem)
  else applyUnassignedMarker(markerItem)

  return isPositioned
}

export const insertAndResequenceRows = (
  reorderedMoveToRows,
  reorderedMoveToRouteStopMarkers,
  formValues,
  selectedRowIdsMap,
  setValue,
  getIsMoveToRow
) => {
  let currentPosition = 0
  const updatedMoveToRows = []
  const updatedMoveToMarkers = []
  const toBeInsertedRowsIds = []
  const markersById = buildMarkerLookup(reorderedMoveToRouteStopMarkers)

  reorderedMoveToRows.forEach((oldRow, i) => {
    const rowItem = { ...oldRow }
    const rowId = get(rowItem, 'id')
    const sourceMarker = lookupSourceMarker(markersById, rowId, reorderedMoveToRouteStopMarkers, i)
    // A row may be markerless (virtualized / off-screen / missing coords). The
    // row's position MUST still be recomputed — otherwise we leak a stale
    // `position` into the data grid and the visible chips skip numbers (CS-2640).
    const markerItem = cloneMarkerForRow(sourceMarker)

    if (getIsMoveToRow(rowItem)) {
      rowItem.position = currentPosition
      if (markerItem) markerItem.properties.position = currentPosition + 1
      currentPosition += 1
    } else {
      toBeInsertedRowsIds.push(rowId)
      const isPositioned = handleInsertedRowPosition({
        rowItem,
        markerItem,
        rowId,
        formValues,
        selectedRowIdsMap,
        setValue,
        currentPosition,
      })
      if (isPositioned) currentPosition += 1
    }
    updatedMoveToRows.push(rowItem)
    if (markerItem) updatedMoveToMarkers.push(markerItem)
  })

  return {
    updatedMoveToRows,
    updatedMoveToMarkers,
    toBeInsertedRowsIds,
  }
}

export const getBulkStopData = row => {
  const accountName = get(row, 'accountName')
  const addressStr = get(row, 'addressStr')
  const addressName = get(row, 'locationName') || get(row, 'addressName', '')
  const id = get(row, 'id')
  const parsedDaysOfWeek = get(row, 'parsedDaysOfWeek', [])
  const position = get(row, 'position', 0) + 1
  const recurrence = get(row, 'recurrence')
  const routeId = get(row, 'routeId')
  const serviceName = get(row, 'serviceName')
  const showWeekSummary = get(row, 'showWeekSummary', false)
  const workOrderStatus = stopStatus(row)
  return {
    accountName,
    addressStr,
    addressName,
    id,
    parsedDaysOfWeek,
    position,
    recurrence,
    routeId,
    serviceName,
    showWeekSummary,
    workOrderStatus,
  }
}

export const getBulkConfirmPermanentMoveData = row => {
  const workOrderId = get(row, 'workOrderId')
  const workOrder = get(row, 'workOrderNumber')
  const workOrderStatus = stopStatus(row)
  const serviceDateStr = get(row, 'serviceDate')
  const serviceDate = getDateObjectFromDateString(serviceDateStr)
  const configuredServiceEventId = get(row, 'configuredServiceEventId')
  const id = [configuredServiceEventId, workOrder].join('|')
  const recurrenceFrequency = get(row, 'recurrenceFrequency', 1)
  const recurrencePer = get(row, 'recurrencePer', '')
  const recurrenceInterval = get(row, 'recurrenceInterval', '')
  const pricingPeriod = getPricingPeriod(recurrencePer, recurrenceInterval)
  const recurrence = pricingPeriod ? `${recurrenceFrequency}x ${pricingPeriod}` : 'On Request'
  return {
    id,
    serviceDate,
    type: PREVIEW_TYPE,
    hierarchy: [configuredServiceEventId, workOrder],
    workOrderId,
    configuredServiceEventId,
    workOrder,
    workOrderStatus,
    recurrence,
  }
}

export const groupFeaturesByIcon = features => {
  const monochromeFeatures = []
  const otherFeatures = []
  if (!Array.isArray(features)) return [monochromeFeatures, otherFeatures]
  features.forEach(feature => {
    if (['monochrome-marker', 'monochrome-container'].includes(feature.properties.icon)) {
      monochromeFeatures.push(feature)
    } else {
      otherFeatures.push(feature)
    }
  })
  return [monochromeFeatures, otherFeatures]
}

export const getSortedSelectedMoveFromRows = selectedRowIdsMap => {
  return sortBy(Array.from(selectedRowIdsMap.values()), ['originalPosition'])
}

export const getUnselectedMoveFromRows = (selectedRowIdsMap, moveFromRows) => {
  return moveFromRows.filter(row => !selectedRowIdsMap.has(row.id)).map((r, i) => ({ ...r, position: i }))
}

export const partitionMoveFromMarkers = (selectedRowIdsMap, moveFromRouteStopMarkers) => {
  return partition(moveFromRouteStopMarkers, marker => selectedRowIdsMap.has(marker.properties.id))
}

export const getCoordsFromMarkers = markers => {
  return markers.map(marker => marker.geometry.coordinates)
}

export const updateMoveFromData = (unselectedMoveFromRows, unselectedMoveFromMarkers, unselectedMoveFromCoords) => {
  const updatedMoveFromRows = unselectedMoveFromRows
  const updatedMoveFromRouteStopMarkers = unselectedMoveFromMarkers.map((r, i) => ({
    ...r,
    properties: { ...r.properties, position: i + 1 },
  }))
  const updatedMoveFromRouteSequenceLine = {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: unselectedMoveFromCoords },
  }
  return { updatedMoveFromRows, updatedMoveFromRouteStopMarkers, updatedMoveFromRouteSequenceLine }
}

export const reorderMoveToData = ({
  moveToRows,
  moveToRouteStopMarkers,
  moveToCoords,
  selectedMoveFromRows,
  selectedMoveFromMarkers,
  selectedMoveFromCoords,
  targetIndex,
  insertArrayAtPosition,
}) => {
  const reorderedMoveToRows = insertArrayAtPosition(moveToRows, selectedMoveFromRows, targetIndex)
  const reorderedMoveToRouteStopMarkers = insertArrayAtPosition(moveToRouteStopMarkers, selectedMoveFromMarkers, targetIndex)
  const reorderedMoveToRouteSequenceLine = {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: insertArrayAtPosition(moveToCoords, selectedMoveFromCoords, targetIndex) },
  }
  return { reorderedMoveToRows, reorderedMoveToRouteStopMarkers, reorderedMoveToRouteSequenceLine }
}

export const preProcessMoveDataOnSequenceChange = ({
  moveFromRouteStopMarkers,
  moveFromRows,
  moveToRouteSequenceLine,
  selectedRowIdsMap,
}) => {
  const selectedMoveFromRows = getSortedSelectedMoveFromRows(selectedRowIdsMap)
  const unselectedMoveFromRows = getUnselectedMoveFromRows(selectedRowIdsMap, moveFromRows)
  const [selectedMoveFromMarkers, unselectedMoveFromMarkers] = partitionMoveFromMarkers(selectedRowIdsMap, moveFromRouteStopMarkers)
  const unselectedMoveFromCoords = getCoordsFromMarkers(unselectedMoveFromMarkers)
  const selectedMoveFromCoords = getCoordsFromMarkers(selectedMoveFromMarkers)
  const moveToCoords = get(moveToRouteSequenceLine, 'geometry.coordinates', [])
  const { updatedMoveFromRows, updatedMoveFromRouteStopMarkers, updatedMoveFromRouteSequenceLine } = updateMoveFromData(
    unselectedMoveFromRows,
    unselectedMoveFromMarkers,
    unselectedMoveFromCoords
  )
  return {
    moveToCoords,
    selectedMoveFromRows,
    selectedMoveFromMarkers,
    selectedMoveFromCoords,
    updatedMoveFromRows,
    updatedMoveFromRouteStopMarkers,
    updatedMoveFromRouteSequenceLine,
  }
}

// Re-exported from routeStopOperations/featureFormatters
export { formatRoutesWithContrastColors }

export const toStopsSequence = toSequencePairs

export const toRowsSequence = rows => toSequencePairs(rows.map(r => r.id))

const stopsLeftToAssign = invalidCount => `${invalidCount} stop left to assign`

export const getBulkSaveTitle = (validCount, invalidCount) => {
  if (invalidCount > 0) return stopsLeftToAssign(invalidCount)
  return validCount > 0 ? 'This does not affect future stops of these services' : 'This does not affect future stops of this service'
}

export const getBulkPublishTitle = (validCount, invalidCount) => {
  if (invalidCount > 0) return stopsLeftToAssign(invalidCount)
  return T.PUBLISH_MASTER_SEQUENCE_TOOLTIP
}

export const getBulkSavePermanentTitle = invalidCount => {
  if (invalidCount > 0) return stopsLeftToAssign(invalidCount)
  return T.SAVE_PERMANENT_TOOLTIP
}

/**
 * Returns only the rows that originated from a different route than the destination.
 * Delegates to routeStopOperations/normalization — same logic, backward-compatible signature.
 */
export const getNewlyAssignedRows = getNewlyAssignedStops

/**
 * Builds the daily assign payload from the full moveToRows list.
 * Delegates to routeStopOperations — same logic, backward-compatible signature.
 */
export const buildDailyAssignPayload = _buildBulkDailyAssignPayload

// Recurring rows only — on-request rows lack a CSE and never enter master.
const collectMasterCseIds = rows =>
  rows.filter(row => row.configuredServiceEventId && !isOnRequestStop(row)).map(row => row.configuredServiceEventId)

/**
 * Builds the payload for assignMasterStops from a set of rows.
 * Thin adapter: filters rows then delegates to routeStopOperations.
 */
export const buildMasterStopAssignPayload = rows => _buildMasterAssignPayload(collectMasterCseIds(rows))

/**
 * Builds the full master resequence payload from all rows on a route.
 * Thin adapter: filters rows then delegates to routeStopOperations.
 */
export const buildMasterResequencePayload = rows => _buildMasterAssignPayload(collectMasterCseIds(rows))

/**
 * Builds the full daily resequence payload from all rows on a route.
 * Thin adapter: extracts IDs then delegates to routeStopOperations.
 */
export const buildDailyResequencePayload = rows => toSequencePairs(rows.filter(row => row.id).map(row => row.id))

/**
 * Returns only the rows that were moved from a different date.
 * Delegates to routeStopOperations/normalization — same logic, backward-compatible signature.
 */
export const getMovedRowsByDate = getMovedStopsByDate

/**
 * Builds the daily assign payload for rows moved between days on the same route.
 * Delegates to routeStopOperations — same logic, backward-compatible signature.
 */
export const buildDailyAssignPayloadByDate = _buildBulkDailyAssignPayloadByDate

/**
 * Computes the correct array index for repositioning a stop to a given
 * display position. Walks the list (excluding the stop being moved) and
 * counts positioned items so that the user-typed position matches the
 * final numbered sequence.
 *
 * @param {Object[]} rows - full moveToRows array
 * @param {number} fromIndex - current index of the stop being moved
 * @param {number} toPosition - 1-indexed display position the user typed
 * @param {Function} isPositioned - (row) => boolean, returns true if the row occupies a numbered position
 * @returns {number} target index for repositionArrayItem
 */
export const getTargetIndexForPosition = (rows, fromIndex, toPosition, isPositioned) => {
  if (!toPosition) return 0

  const target = Math.max(1, Math.min(toPosition, rows.length))
  const rowsWithoutCurrent = [...rows.slice(0, fromIndex), ...rows.slice(fromIndex + 1)]

  let positionedCount = 0
  for (let i = 0; i < rowsWithoutCurrent.length; i++) {
    if (isPositioned(rowsWithoutCurrent[i])) {
      positionedCount += 1
    }
    if (positionedCount >= target) {
      return i
    }
  }
  return rowsWithoutCurrent.length
}

/**
 * Reorders master stops to mirror the daily arrangement.
 * Re-exported from routeStopOperations/payloads.
 */
export { buildMasterResequenceFromDaily }

// Re-exported from routeStopOperations/featureFormatters
export { formatGeojsonDataBetweenDays }
